/**
 * Buddy Manager
 * Handles automated fishing buddy logic
 */

import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { FishingBuddy, DEFAULT_STATS, calculateXPForLevel, awardXP, XP_BY_QUALITY, calculateStatMultipliers, Quality, EquipmentSlot, GameEvent } from '@/types';
import { fishingEngine } from '../fishing/FishingEngine';
import { equipmentManager } from '../equipment/EquipmentManager';
import { mentorManager } from '../mentoring/MentorManager';
import { BALANCE_CONFIG } from '@/data/balance/balanceConfig';
import { getZoneDefinition } from '@/data/zones/zoneData';
import { getFishDefinition } from '@/data';

export class BuddyManager {
  private updateIntervalId: number | null = null;
  private readonly UPDATE_FREQUENCY = 1000; // Check every second
  private hasShownInventoryFullWarning = false;
  private unsubscribeFishSold: (() => void) | null = null;

  constructor() {
    console.log('🧑‍🤝‍🧑 BuddyManager initialized');

    // Listen for fish sold events to resume buddies when space becomes available
    this.unsubscribeFishSold = eventBus.on(GameEvent.FISH_SOLD, () => this.onInventorySpaceAvailable());
  }

  /**
   * Start the buddy update loop
   */
  start(): void {
    if (this.updateIntervalId !== null) {
      console.warn('BuddyManager already started');
      return;
    }

    console.log('🧑‍🤝‍🧑 Starting buddy auto-fishing');
    this.updateIntervalId = window.setInterval(() => {
      this.update();
    }, this.UPDATE_FREQUENCY);
  }

  /**
   * Stop the buddy update loop
   */
  stop(): void {
    if (this.updateIntervalId !== null) {
      window.clearInterval(this.updateIntervalId);
      this.updateIntervalId = null;
      console.log('🧑‍🤝‍🧑 Stopped buddy auto-fishing');
    }

    // Unsubscribe from events
    if (this.unsubscribeFishSold) {
      this.unsubscribeFishSold();
      this.unsubscribeFishSold = null;
    }
  }

  /**
   * Main update loop - check all buddies for fishing
   */
  private update(): void {
    const state = useGameStore.getState();
    const buddies = state.playerState.fishingBuddies;

    if (!buddies || buddies.length === 0) {
      return; // No buddies to update
    }

    // Check if inventory is full - if so, pause all buddies
    const isInventoryFull = state.playerState.inventory.currentCount >= state.playerState.inventory.capacity;

    if (isInventoryFull) {
      // Show warning only once
      if (!this.hasShownInventoryFullWarning) {
        console.log('🧑‍🤝‍🧑 Inventory full - buddies paused');
        eventBus.emit(GameEvent.INVENTORY_FULL, {});
        this.hasShownInventoryFullWarning = true;
      }
      // Don't attempt any fishing while inventory is full
      return;
    }

    const now = Date.now();

    buddies.forEach((buddy, index) => {
      if (!buddy.hired || !buddy.enabled) {
        return; // Skip unhired or disabled buddies
      }

      // Check if it's time for this buddy to fish
      if (buddy.nextCatchAt && now >= buddy.nextCatchAt) {
        this.performBuddyFishing(index);
      }
    });
  }

  /**
   * Perform a fishing action for a specific buddy
   */
  private performBuddyFishing(buddyIndex: number): void {
    const state = useGameStore.getState();
    const buddy = state.playerState.fishingBuddies[buddyIndex];

    if (!buddy || !buddy.hired || !buddy.enabled) {
      return;
    }

    // Calculate buddy's progression stat multipliers
    const multipliers = calculateStatMultipliers(buddy.progression.stats);

    // Calculate buddy's equipment stats
    const equipStats = equipmentManager.calculateBuddyEquipmentStats(buddy.slotNumber);

    // Apply buddy's progression + equipment bonuses to modifiers
    // Catch chance: Base buddy reduction offset by progression + equipment catch chance bonus
    const baseCatchReduction = BALANCE_CONFIG.FISHING_BUDDY_CATCH_REDUCTION;
    const progressionCatchBonus = (multipliers.catchChance - 1) * 0.5; // 50% effectiveness for buddies
    const equipmentCatchBonus = equipStats.catchChance || 0;
    const finalCatchReduction = Math.max(0, baseCatchReduction - progressionCatchBonus - equipmentCatchBonus);

    // Equipment fishing speed reduces time multiplier
    const equipmentSpeedBonus = equipStats.fishingSpeed || 0;
    const timeMultiplier = BALANCE_CONFIG.FISHING_BUDDY_FISHING_TIME_MULT * (1 - equipmentSpeedBonus);

    // Apply mentor bonus (active play buffs buddy catch chance)
    const mentorMultiplier = mentorManager.getMentorMultiplier();
    const mentorQualityBonus = mentorManager.getMentorQualityBonus();

    // Attempt to catch a fish using the fishing engine
    const caughtFish = fishingEngine.attemptCatch({
      isBuddy: true,
      buddyModifiers: {
        timeMultiplier,
        catchReduction: finalCatchReduction / mentorMultiplier, // Mentor reduces effective catch penalty
        qualityCap: Quality.RARE, // Buddies can't catch epic+
        qualityBonus: (multipliers.qualityBonus || 0) + (equipStats.qualityBonus || 0) + mentorQualityBonus,
      },
    });

    const now = Date.now();

    if (caughtFish) {
      // Add fish to inventory
      const added = state.addFishToInventory(caughtFish);

      if (added) {
        // Consume buddy's bait on successful catch
        equipmentManager.consumeBait(buddy.slotNumber);

        // Track discovery
        state.trackFishDiscovery(caughtFish);

        // Calculate fish value for stats tracking (with buddy's value bonus)
        const definition = getFishDefinition(caughtFish.definitionId);
        const baseValue = definition.baseValue;
        // Simple value calculation (quality multipliers: poor=0.5, normal=1, good=1.5, excellent=2)
        const qualityMult = caughtFish.quality === Quality.COMMON ? 0.75 : caughtFish.quality === Quality.UNCOMMON ? 1 : caughtFish.quality === Quality.RARE ? 1.5 : 2;
        const valueWithQuality = baseValue * qualityMult;
        // Apply buddy's value bonus from progression + equipment
        const totalValueBonus = multipliers.valueBonus + (equipStats.valueBonus || 0);
        const estimatedValue = Math.floor(valueWithQuality * (1 + totalValueBonus));

        // Update buddy stats and award XP
        this.updateBuddyStats(buddyIndex, estimatedValue);
        this.awardBuddyXP(buddyIndex, caughtFish.quality);

        // Emit event for UI feedback
        eventBus.emit(GameEvent.BUDDY_CAUGHT_FISH, {
          buddySlot: buddy.slotNumber,
          fish: caughtFish,
          timestamp: now,
        });

        console.log(`🧑‍🤝‍🧑 Buddy ${buddy.slotNumber} caught: ${caughtFish.definitionId} (${caughtFish.quality})`);
      }
    }

    // Schedule next catch attempt
    this.scheduleBuddyNextCatch(buddyIndex);
  }

  /**
   * Called when inventory space becomes available (after selling fish)
   * Resumes all paused buddies
   */
  private onInventorySpaceAvailable(): void {
    const state = useGameStore.getState();

    // Check if inventory actually has space now
    const hasSpace = state.playerState.inventory.currentCount < state.playerState.inventory.capacity;

    if (hasSpace && this.hasShownInventoryFullWarning) {
      console.log('🧑‍🤝‍🧑 Inventory space available - resuming buddies');
      this.hasShownInventoryFullWarning = false;

      // Resume all buddies by scheduling their next catch
      const now = Date.now();

      useGameStore.setState((draft) => {
        draft.playerState.fishingBuddies.forEach((buddy) => {
          if (buddy.hired && buddy.enabled) {
            // Schedule immediate or short-delay next catch
            buddy.nextCatchAt = now + 1000; // 1 second delay to resume
          }
        });
      });
    }
  }

  /**
   * Schedule the next catch time for a buddy (with progression bonuses)
   */
  private scheduleBuddyNextCatch(buddyIndex: number): void {
    const now = Date.now();
    const state = useGameStore.getState();
    const buddy = state.playerState.fishingBuddies[buddyIndex];

    if (!buddy) return;

    // Calculate interval with buddy's progression stat multipliers + equipment
    const multipliers = calculateStatMultipliers(buddy.progression.stats);
    const equipStats = equipmentManager.calculateBuddyEquipmentStats(buddy.slotNumber);
    const baseIntervalMs = BALANCE_CONFIG.FISHING_BUDDY_INTERVAL * 1000;

    // Catch speed multiplier reduces the interval (faster fishing)
    // Equipment fishing speed also reduces interval
    const equipmentSpeedFactor = 1 - (equipStats.fishingSpeed || 0);
    const modifiedIntervalMs = (baseIntervalMs * equipmentSpeedFactor) / multipliers.catchSpeed;

    useGameStore.setState((draft) => {
      if (draft.playerState.fishingBuddies[buddyIndex]) {
        draft.playerState.fishingBuddies[buddyIndex].lastCatchAt = now;
        draft.playerState.fishingBuddies[buddyIndex].nextCatchAt = now + modifiedIntervalMs;
      }
    });
  }

  /**
   * Update buddy statistics
   */
  private updateBuddyStats(buddyIndex: number, fishValue: number): void {
    useGameStore.setState((draft) => {
      const buddy = draft.playerState.fishingBuddies[buddyIndex];
      if (buddy) {
        buddy.totalFishCaught++;
        buddy.totalValueGenerated += fishValue;
      }
    });
  }

  /**
   * Award XP to a buddy for catching a fish
   */
  private awardBuddyXP(buddyIndex: number, quality: string): void {
    const xpGained = XP_BY_QUALITY[quality] || 1;

    useGameStore.setState((draft) => {
      const buddy = draft.playerState.fishingBuddies[buddyIndex];
      if (buddy) {
        const oldLevel = buddy.progression.progress.level;
        const newProgression = awardXP(buddy.progression, xpGained);
        buddy.progression = newProgression;

        // Log level-up if it occurred
        if (newProgression.progress.level > oldLevel) {
          const buddyName = buddy.name || `Buddy ${buddy.slotNumber}`;
          console.log(
            `🎉 LEVEL UP! ${buddyName} reached level ${newProgression.progress.level}! ` +
            `Stat points available: ${newProgression.progress.unspentStatPoints}`
          );
          eventBus.emit(GameEvent.BUDDY_LEVEL_UP, {
            buddyName,
            buddyType: 'fishing',
            newLevel: newProgression.progress.level,
            oldLevel,
          });
        }
      }
    });
  }

  /**
   * Hire a new buddy (unlock slot)
   */
  hireBuddy(slotNumber: number): boolean {
    const state = useGameStore.getState();
    const zone = getZoneDefinition(state.playerState.currentZone);

    // Validate slot number
    if (slotNumber < 1 || slotNumber > zone.maxBuddies) {
      console.error(`Invalid buddy slot: ${slotNumber}`);
      return false;
    }

    // Check if already hired
    const existingBuddy = state.playerState.fishingBuddies.find(
      (b) => b.slotNumber === slotNumber
    );
    if (existingBuddy?.hired) {
      console.warn(`Buddy ${slotNumber} already hired`);
      return false;
    }

    // Get cost for this slot
    const cost = this.getBuddyCost(slotNumber);

    // Check if player can afford
    if (state.playerState.currency < cost) {
      console.warn(`Not enough currency to hire buddy ${slotNumber}`);
      return false;
    }

    // Deduct cost
    state.addCurrency(-cost);

    // Create and add the buddy
    const newBuddy: FishingBuddy = {
      slotNumber,
      hired: true,
      enabled: true,
      lastCatchAt: null,
      nextCatchAt: Date.now() + BALANCE_CONFIG.FISHING_BUDDY_INTERVAL * 1000,
      totalFishCaught: 0,
      totalValueGenerated: 0,
      progression: {
        stats: DEFAULT_STATS,
        progress: {
          level: 1,
          currentXP: 0,
          xpToNextLevel: calculateXPForLevel(2),
          unspentStatPoints: 0,
        },
      },
      equippedGear: {
        [EquipmentSlot.ROD]: null,
        [EquipmentSlot.REEL]: null,
        [EquipmentSlot.BAIT]: null,
        [EquipmentSlot.LINE]: null,
        [EquipmentSlot.TACKLE]: null,
      },
    };

    useGameStore.setState((draft) => {
      draft.playerState.fishingBuddies.push(newBuddy);
    });

    eventBus.emit(GameEvent.BUDDY_HIRED, { slotNumber, cost });
    console.log(`🧑‍🤝‍🧑 Hired buddy ${slotNumber} for ${cost} gold`);

    return true;
  }

  /**
   * Toggle buddy enabled/disabled
   */
  toggleBuddy(slotNumber: number): void {
    useGameStore.setState((draft) => {
      const buddy = draft.playerState.fishingBuddies.find((b) => b.slotNumber === slotNumber);
      if (buddy) {
        buddy.enabled = !buddy.enabled;
        console.log(`🧑‍🤝‍🧑 Buddy ${slotNumber} ${buddy.enabled ? 'enabled' : 'disabled'}`);
      }
    });
  }

  /**
   * Rename a buddy
   */
  renameBuddy(slotNumber: number, newName: string): void {
    useGameStore.setState((draft) => {
      const buddy = draft.playerState.fishingBuddies.find((b) => b.slotNumber === slotNumber);
      if (buddy) {
        buddy.name = newName.trim() || undefined; // Store undefined if empty
        console.log(`🧑‍🤝‍🧑 Buddy ${slotNumber} renamed to: ${buddy.name || 'Buddy ' + slotNumber}`);
      }
    });
  }

  /**
   * Get cost for a buddy slot
   */
  getBuddyCost(slotNumber: number): number {
    if (slotNumber < 1 || slotNumber > BALANCE_CONFIG.FISHING_BUDDY_COSTS.length) {
      return Infinity;
    }
    return BALANCE_CONFIG.FISHING_BUDDY_COSTS[slotNumber - 1];
  }

  /**
   * Check if player can afford a buddy slot
   */
  canAffordBuddy(slotNumber: number): boolean {
    const state = useGameStore.getState();
    const cost = this.getBuddyCost(slotNumber);
    return state.playerState.currency >= cost;
  }

  /**
   * Get buddy by slot number
   */
  getBuddy(slotNumber: number): FishingBuddy | undefined {
    const state = useGameStore.getState();
    return state.playerState.fishingBuddies.find((b) => b.slotNumber === slotNumber);
  }

  /**
   * Get all hired buddies
   */
  getHiredBuddies(): FishingBuddy[] {
    const state = useGameStore.getState();
    return state.playerState.fishingBuddies.filter((b) => b.hired);
  }
}

// Singleton instance
export const buddyManager = new BuddyManager();
